import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable, FilaExportable } from "@/componentes/exportar/modelo"
import { KpiAgenda } from "@/componentes/kpi/KpiTalento"
import type { Evento, ResultadoProgramacion } from "@/componentes/talento/planificacion/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * La agenda laboral del personal activo: cuándo nace cada período de vacaciones, quién
 * sale y vuelve, cuándo toca la garantía trimestral y el límite de las utilidades.
 *
 * ⚠ **Se calcula al pedirla** desde las fechas de ingreso (`/planificacion-laboral/agenda`):
 * no hay una tabla de eventos que mantener, y corregir una fecha de ingreso corrige la
 * agenda.
 *
 * Debajo, los días de utilidades que la empresa da cada ejercicio (art. 131: 30 a 120).
 */

const TIPO: Record<Evento["tipo"], { texto: string; tono: "info" | "aviso" | "exito" | "neutro" }> =
  {
    VACACIONES_NACE: { texto: "Nace período", tono: "aviso" },
    VACACIONES_INICIO: { texto: "Sale de vacaciones", tono: "info" },
    VACACIONES_REINTEGRO: { texto: "Se reintegra", tono: "exito" },
    GARANTIA: { texto: "Garantía", tono: "neutro" },
    UTILIDADES: { texto: "Utilidades", tono: "neutro" },
    UTILIDADES_PAGO: { texto: "Pago de utilidades", tono: "info" },
  }

const HORIZONTES = [30, 90, 180, 365] as const

const MES = new Intl.DateTimeFormat("es-VE", { month: "long", year: "numeric" })

function mesDe(fecha: string): string {
  const [anio, mes] = fecha.split("-").map(Number)
  return MES.format(new Date(anio ?? 2000, (mes ?? 1) - 1, 1))
}

/** La agenda tal como se ve: un rótulo por mes y debajo sus eventos. */
function documentoDeAgenda(eventos: Evento[], dias: number): DocumentoExportable {
  const filas: FilaExportable[] = []
  let mesActual = ""
  for (const e of eventos) {
    const mes = mesDe(e.fecha)
    if (mes !== mesActual) {
      mesActual = mes
      filas.push({
        celdas: [mes.charAt(0).toUpperCase() + mes.slice(1), null, null, null],
        grupo: true,
      })
    }
    filas.push({ celdas: [e.fecha.slice(0, 10), TIPO[e.tipo].texto, e.persona, e.titulo] })
  }
  return {
    titulo: "Agenda laboral",
    subtitulo: `Próximos ${dias} días · personal activo`,
    secciones: [
      {
        titulo: "Agenda laboral",
        columnas: [
          { titulo: "Fecha", tipo: "fecha" },
          { titulo: "Evento", tipo: "texto" },
          { titulo: "Trabajador", tipo: "texto" },
          { titulo: "Detalle", tipo: "texto" },
        ],
        filas,
      },
    ],
  }
}

export function AgendaLaboral() {
  const [dias, setDias] = useState<(typeof HORIZONTES)[number]>(90)
  const consulta = useQuery({
    queryKey: ["agenda-laboral", dias],
    queryFn: () => api.get<Evento[]>(`/api/v1/planificacion-laboral/agenda?dias=${dias}`),
  })
  const eventos = consulta.data ?? []
  const meses = [...new Set(eventos.map((e) => mesDe(e.fecha)))]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="horizonte">Próximos</label>
        <select
          id="horizonte"
          className="campo w-auto"
          value={dias}
          onChange={(e) => setDias(Number(e.target.value) as (typeof HORIZONTES)[number])}
        >
          {HORIZONTES.map((h) => (
            <option key={h} value={h}>
              {h} días
            </option>
          ))}
        </select>
        {eventos.length > 0 && (
          <span className="ml-auto">
            <Exportar titulo="Agenda laboral" documento={() => documentoDeAgenda(eventos, dias)} />
          </span>
        )}
      </div>

      {consulta.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Calculando la agenda…
        </p>
      )}
      {consulta.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar la agenda.
        </p>
      )}
      {consulta.isSuccess && <KpiAgenda eventos={eventos} dias={dias} />}
      {consulta.isSuccess && eventos.length === 0 && (
        <p className="tarjeta p-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          Nada en este plazo. Sólo entra el personal activo con fecha de ingreso en su ficha.
        </p>
      )}

      {meses.map((mes) => (
        <section key={mes} aria-label={mes}>
          <h2 className="rotulo mb-2 capitalize">{mes}</h2>
          <ul className="space-y-2">
            {eventos
              .filter((e) => mesDe(e.fecha) === mes)
              .map((e) => (
                <li
                  key={`${e.fecha}-${e.persona_id}-${e.tipo}`}
                  className="superficie flex flex-wrap items-center gap-2 p-3 text-sm"
                >
                  <span className="w-24 shrink-0 tabular-nums">{comoFecha(e.fecha)}</span>
                  <Insignia tono={TIPO[e.tipo].tono}>{TIPO[e.tipo].texto}</Insignia>
                  <strong>{e.persona}</strong>
                  <span
                    className="min-w-0 basis-full sm:basis-auto"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    {e.titulo}
                  </span>
                </li>
              ))}
          </ul>
        </section>
      ))}

      <EjerciciosUtilidades />
      <UtilidadesDeTodos />
    </div>
  )
}

type Ejercicio = { anio: number; dias: number; notas: string | null }

function EjerciciosUtilidades() {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "th_calculo:administrar")
  const [anio, setAnio] = useState(new Date().getFullYear())
  const [dias, setDias] = useState("30")

  const consulta = useQuery({
    queryKey: ["utilidades-ejercicios"],
    queryFn: () => api.get<Ejercicio[]>("/api/v1/utilidades/ejercicios"),
  })
  const fijar = useMutation({
    mutationFn: () => api.put(`/api/v1/utilidades/ejercicios/${anio}`, { dias: Number(dias) }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ["utilidades-ejercicios"] })
      cliente.invalidateQueries({ queryKey: ["planificacion-laboral"] })
    },
  })

  return (
    <section aria-label="Días de utilidades por ejercicio" className="space-y-2">
      <h2 className="rotulo">Días de utilidades por ejercicio</h2>
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Entre 30 y 120 días (LOTTT art. 131). Un ejercicio sin días fijados se calcula con el mínimo
        legal.
      </p>
      {(consulta.data ?? []).length > 0 && (
        <ul className="text-sm tabular-nums">
          {consulta.data?.map((e) => (
            <li key={e.anio}>
              {e.anio}: {e.dias} días
            </li>
          ))}
        </ul>
      )}
      {puede && (
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase">Ejercicio</span>
            <input
              type="number"
              className="campo w-28 tabular-nums"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase">Días</span>
            <input
              type="number"
              min={30}
              max={120}
              className="campo w-24 tabular-nums"
              value={dias}
              onChange={(e) => setDias(e.target.value)}
            />
          </label>
          <Boton
            tamano="sm"
            variante="primario"
            disabled={fijar.isPending || Number(dias) < 30 || Number(dias) > 120}
            onClick={() => fijar.mutate()}
          >
            Guardar
          </Boton>
        </div>
      )}
      {fijar.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {fijar.error instanceof ErrorApi ? fijar.error.message : "No se pudo guardar"}
        </p>
      )}
    </section>
  )
}

/**
 * El gesto de diciembre: programar las utilidades del ejercicio a TODO el personal activo.
 *
 * ⚠ **No falla entero por una ficha**: a quien le falta el contrato o la fecha de ingreso
 * se le salta y se dice por qué; a quien ya las tenía programadas, también. Cada
 * programada emite su documento de cálculo.
 */
function UtilidadesDeTodos() {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const [anio, setAnio] = useState(new Date().getFullYear())
  const [fecha, setFecha] = useState("")
  const programar = useMutation({
    mutationFn: () =>
      api.post<ResultadoProgramacion[]>(`/api/v1/utilidades/${anio}/programar-todos`, {
        fecha_pago: fecha,
      }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ["planificacion-laboral"] })
      cliente.invalidateQueries({ queryKey: ["agenda-laboral"] })
      cliente.invalidateQueries({ queryKey: ["documentos-th"] })
    },
  })
  if (!tienePermiso(usuario, "th_calculo:administrar")) return null
  const resultados = programar.data ?? []
  const hechas = resultados.filter((r) => r.programada_id !== null)
  const omitidas = resultados.filter((r) => r.programada_id === null)

  return (
    <section aria-label="Programar utilidades a todo el personal" className="space-y-2">
      <h2 className="rotulo">Programar utilidades a todo el personal</h2>
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Congela el cálculo de cada persona activa con los días del ejercicio y su salario legal de
        hoy, y emite su documento. Lo ya programado no se toca.
      </p>
      <div className="flex flex-wrap items-end gap-2 text-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase">Ejercicio</span>
          <input
            type="number"
            className="campo w-28 tabular-nums"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase">Fecha de pago</span>
          <input
            type="date"
            className="campo w-auto tabular-nums"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </label>
        <Boton
          tamano="sm"
          variante="primario"
          disabled={fecha === "" || programar.isPending}
          onClick={() => programar.mutate()}
        >
          {programar.isPending ? "Programando…" : "Programar a todos"}
        </Boton>
      </div>
      {programar.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {programar.error instanceof ErrorApi ? programar.error.message : "No se pudo programar"}
        </p>
      )}
      {programar.isSuccess && (
        <div className="space-y-1 text-sm" role="status">
          <p>
            Programadas: {hechas.length}
            {omitidas.length > 0 && ` · sin programar: ${omitidas.length}`}
          </p>
          {omitidas.length > 0 && (
            <ul className="list-disc space-y-1 pl-5" style={{ color: "var(--warning)" }}>
              {omitidas.map((r) => (
                <li key={r.persona_id}>
                  {r.persona}: {r.motivo}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
